import { useEffect, useState } from "react";
import { displayDate } from "@/lib/dates";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ListChecks, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-crew task picker on the Dispatch board. For each dispatched worker,
 * office picks which of the job's tasks they should do that day —
 * stored as task_ids on their assignment row.
 */
export function DispatchCrewTasks({ job, crew, onSaved }) {
  const [editing, setEditing] = useState(null); // { user, assignmentId, taskIds }
  if (!crew.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-zinc-200" data-testid={`dispatch-tasks-${job.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <ListChecks className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <div className="label-tag !text-zinc-500">Tasks</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {crew.map((c) => (
          <button
            key={c.assignmentId}
            type="button"
            onClick={() => setEditing(c)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${
              c.taskIds?.length
                ? "bg-[#09090B] text-white border-[#09090B] hover:bg-zinc-800"
                : "bg-white text-zinc-600 border-zinc-300 hover:border-[#FF5F15]"
            }`}
            data-testid={`dispatch-task-btn-${c.assignmentId}`}
          >
            {c.user.name.split(" ")[0]}
            <span className={c.taskIds?.length ? "text-[#FF5F15]" : "text-zinc-400"}>
              · {c.taskIds?.length || 0} task{(c.taskIds?.length || 0) === 1 ? "" : "s"}
            </span>
          </button>
        ))}
      </div>
      {editing && (
        <TaskPickerDialog
          job={job}
          crewMember={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onSaved?.(); }}
        />
      )}
    </div>
  );
}

function TaskPickerDialog({ job, crewMember, onClose, onSaved }) {
  const [tasks, setTasks] = useState(null);
  const [selected, setSelected] = useState(new Set(crewMember.taskIds || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${job.id}/tasks`)
      .then((r) => setTasks(r.data.items || []))
      .catch(() => setTasks([]));
  }, [job.id]);

  const flip = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/assignments/${crewMember.assignmentId}`, { task_ids: [...selected] });
      toast.success(`Tasks updated for ${crewMember.user.name.split(" ")[0]}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tasks");
      setSaving(false);
    }
  };

  const openTasks = (tasks || []).filter((t) => t.status !== "done");
  const doneTasks = (tasks || []).filter((t) => t.status === "done");

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="dispatch-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold uppercase tracking-tight">
            Tasks for {crewMember.user.name.split(" ")[0]} — {job.title}
          </DialogTitle>
        </DialogHeader>
        {tasks === null ? (
          <div className="text-xs text-zinc-400 py-4">Loading tasks…</div>
        ) : tasks.length === 0 ? (
          <div className="text-sm text-zinc-500 py-3" data-testid="dispatch-task-dialog-empty">
            This job has no tasks yet. Add tasks on the{" "}
            <a href={`/app/jobs/${job.id}`} className="text-[#FF5F15] font-bold">job page</a> first.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {openTasks.map((t) => (
              <TaskRow key={t.id} task={t} checked={selected.has(t.id)} onFlip={() => flip(t.id)} />
            ))}
            {doneTasks.length > 0 && (
              <>
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 pt-2">Already completed</div>
                {doneTasks.map((t) => (
                  <TaskRow key={t.id} task={t} checked={selected.has(t.id)} onFlip={() => flip(t.id)} done />
                ))}
              </>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={onClose}
                  data-testid="dispatch-task-dialog-cancel">Cancel</Button>
          {tasks?.length > 0 && (
            <Button size="sm" onClick={save} disabled={saving}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider"
                    data-testid="dispatch-task-dialog-save">
              {saving ? "Saving…" : `Assign ${selected.size} task${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ task, checked, onFlip, done }) {
  return (
    <button
      type="button"
      onClick={onFlip}
      className={`w-full flex items-start gap-2.5 p-2 border text-left transition-colors ${
        checked ? "border-[#FF5F15] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"
      } ${done ? "opacity-60" : ""}`}
      data-testid={`dispatch-task-option-${task.id}`}
    >
      <span className={`mt-0.5 w-4 h-4 shrink-0 border-2 flex items-center justify-center ${
        checked ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-400"
      }`}>
        {checked && <Check className="w-3 h-3" />}
      </span>
      <span className={`text-sm ${done ? "line-through text-zinc-400" : ""}`}>{task.title}{task.scheduled_date && <span className="block text-xs text-muted-foreground">{displayDate(task.scheduled_date)} · {task.recurrence?.frequency || "Scheduled"}</span>}</span>
    </button>
  );
}
